/**
 * Sign-up completion page (Figma 1541-25505). Clean centered layout: green check,
 * title + gray subtitle, no illustration, a primary "Find My First Class" pill
 * (1:N group -> /{lang}/courses) and a secondary "Browse tutors first" text link
 * (1:1 private -> /{lang}/real-time-lectures). No auto-redirect countdown.
 * The enterprise-invitation flow keeps its original layout + manual redirect link.
 */
import { useState, useEffect } from 'react'
import { getI18nLanguageFromLocalStorage } from '../../helpers/localStorageHelper';
import { translate } from "../../localization/translateService";
import "./SignUpSuccess.css";

const CheckBadge = () => (
  <svg width="32" height="32" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.5 0C6.76875 0 0.5 6.26875 0.5 14C0.5 21.7312 6.76875 28 14.5 28C22.2312 28 28.5 21.7312 28.5 14C28.5 6.26875 22.2312 0 14.5 0ZM20.5469 9.42813L13.9656 18.5531C13.8736 18.6815 13.7524 18.7861 13.6119 18.8583C13.4714 18.9305 13.3157 18.9681 13.1578 18.9681C12.9999 18.9681 12.8442 18.9305 12.7037 18.8583C12.5632 18.7861 12.442 18.6815 12.35 18.5531L8.45312 13.1531C8.33438 12.9875 8.45312 12.7563 8.65625 12.7563H10.1219C10.4406 12.7563 10.7437 12.9094 10.9312 13.1719L13.1562 16.2594L18.0688 9.44687C18.2563 9.1875 18.5562 9.03125 18.8781 9.03125H20.3438C20.5469 9.03125 20.6656 9.2625 20.5469 9.42813Z" fill="#22C55E" />
  </svg>
);

const SignUpSuccess = ({ props }) => {
  const currentLanguage = getI18nLanguageFromLocalStorage();
  const [redirectUrl, setRedirectUrl] = useState('');
  const [invitationCode, setInvitationCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(true);
  const [labels, setLabels] = useState({
    title: "You're ready to start learning Korean!",
    subtitle: "From TOPIK prep to everyday conversation — find the right class for you",
    findFirstClass: 'Find My First Class',
    browseTutors: 'Browse tutors first',
  });

  // Completion CTAs (relative + language-aware so they work per environment).
  const groupClassUrl = `/${currentLanguage}/courses`;
  const browseTutorsUrl = `/${currentLanguage}/courses?page=1&rows=12&sortBy=newest`;

  // Tutor sign-ups are forwarded into the tutor-application funnel (see effect + render).
  const isTrainerSignup = new URLSearchParams(window.location.search).get('isTrainerRegisterForm') === 'True';

  useEffect(() => {
    const loadTranslationAsync = async () => {
      if (props?.errorMessage) {
        setErrorMessage(await translate(props?.errorMessage));
      }
      const [title, subtitle, findFirstClass, browseTutors] = await Promise.all([
        translate("UMBRACO_LANDING.SIGNUP_SUCCESS.TITLE", false, "You're ready to start learning Korean!"),
        translate("UMBRACO_LANDING.SIGNUP_SUCCESS.SUBTITLE", false, "From TOPIK prep to everyday conversation — find the right class for you"),
        translate("UMBRACO_LANDING.SIGNUP_SUCCESS.FIND_FIRST_CLASS", false, "Find My First Class"),
        translate("UMBRACO_LANDING.SIGNUP_SUCCESS.BROWSE_TUTORS", false, "Browse tutors first"),
      ]);
      setLabels({ title, subtitle, findFirstClass, browseTutors });
    };
    loadTranslationAsync();

    // Resolve the redirect URL used by the invitation / error flows and the tutor forward.
    const urlParams = new URLSearchParams(window.location.search);
    const invitationCodeValue = urlParams.get('invitationCode');

    let url = !isTrainerSignup
      ? `/auth/login?lang=${currentLanguage}`
      : `/auth/login?lang=${currentLanguage}&returnUrl=/${currentLanguage}/tutor-onboarding?isOpenForm=true`;

    if (invitationCodeValue) {
      setInvitationCode(invitationCodeValue);
      url = "/";
    }

    setRedirectUrl(url);

    // Tutor sign-ups must be forwarded into the tutor-application funnel so the
    // registration form auto-opens on /tutor-onboarding (restores the pre-redesign
    // step; learners keep the new completion page). Skip on error / invitation flows.
    if (isTrainerSignup && !invitationCodeValue && !props?.errorMessage) {
      window.location.replace(url);
    }
  }, [])

  // Error state — show the message + a manual link (no countdown).
  if (props.errorMessage) {
    return (
      <div className="signup-success-page text-center">
        <div className="register-success w-full flex flex-column align-items-center p-5 pb-8 text-center">
          <div>
            <img style={{ maxHeight: '200px' }} src={props?.image} alt="" />
          </div>
          <div className="keduall-typograph text-16-normal-400 mt-5 mb-3 flex items-center justify-center text-center text-error-txt">
            {errorMessage}
          </div>
          <small><a style={{ color: 'var(--text-info)' }} href={redirectUrl}>{props?.btnForceRedirect}</a></small>
        </div>
      </div>
    );
  }

  // Enterprise-invitation flow — keep the original layout + manual redirect link.
  if (invitationCode) {
    return (
      <div className="signup-success-page text-center">
        <div className="register-success w-full flex flex-column align-items-center p-5 pb-8 text-center">
          <CheckBadge />
          <h3 className="keduall-typography heading-h3-semibold-600 mt-3 mb-5 text-center">{props?.invitationPageTitle}</h3>
          <div>
            <img style={{ maxHeight: '200px' }} src={props?.image} alt="" />
          </div>
          <div className="keduall-typograph text-16-normal-400 mt-5 mb-3 flex items-center justify-center text-center">
            {props?.invitationPageDescription}
          </div>
          <small><a style={{ color: 'var(--text-info)' }} href={redirectUrl}>{props?.btnForceRedirect}</a></small>
        </div>
      </div>
    );
  }

  // Tutor sign-ups are being forwarded (see effect) — render nothing so the learner
  // completion page never flashes before the redirect fires.
  if (isTrainerSignup) {
    return null;
  }

  // Normal completion — Figma 1541-25505.
  return (
    <div className="signup-success-page text-center">
      <div className="register-success w-full flex flex-column align-items-center p-5 pb-8 text-center">
        <CheckBadge />
        <h3 className="signup-success-title">{labels.title}</h3>
        <p className="signup-success-subtitle">{labels.subtitle}</p>
        <div className="signup-success-actions">
          <a className="signup-success-btn signup-success-btn--primary" href={groupClassUrl}>{labels.findFirstClass}</a>
          <a className="signup-success-link" href={browseTutorsUrl}>{labels.browseTutors}</a>
        </div>
      </div>
    </div>
  );
}

export default SignUpSuccess
